// frontend/src/pages/PdfSettingsPage.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PdfSettings, DEFAULT_PDF_SETTINGS, Seller } from '../types/invoiceTypes';
import {
  IconTheme,
  IconImage,
  IconFont,
  IconTable,
  IconQrCode,
  IconSave,
  IconUndo,
  IconView,
  IconUser,
  IconArrowLeft,
} from '../icons';

// Beispieldaten für die Vorschau
const PREVIEW_DATA = {
  seller: {
    name: 'Musterfirma GmbH',
    street: 'Musterstraße 123',
    zip: '12345',
    city: 'Musterstadt',
    country: 'DE',
    vatNumber: 'DE123456789',
    commercialRegisterNumber: 'HRB 12345',
    commercialRegisterLocation: 'Musterstadt',
    contact: {
      name: 'Max Mustermann',
      phone: '+49 123 456789',
      email: 'info@musterfirma.de',
      website: 'www.musterfirma.de',
    },
    bankDetails: {
      iban: 'DE89 3704 0044 0532 0130 00',
      bic: 'COBADEFFXXX',
      bankName: 'Commerzbank',
    },
  },
  buyer: {
    companyName: 'Kunde AG',
    street: 'Kundenweg 42',
    zip: '54321',
    city: 'Kundenstadt',
    country: 'DE',
  },
  invoiceNumber: 'RE2026-01-0001',
  invoiceDate: '12.01.2026',
  dueDate: '11.02.2026',
  deliveryDate: '10.01.2026',
  items: [
    { name: 'Beratungsleistung', description: 'Projektberatung Januar 2026', quantity: 8, netPrice: 120, taxRate: 19, unitCode: 'HUR' },
    { name: 'Softwareentwicklung', description: 'Feature-Entwicklung', quantity: 16, netPrice: 95, taxRate: 19, unitCode: 'HUR' },
    { name: 'Dokumentation', quantity: 1, netPrice: 250, taxRate: 19, unitCode: 'H87' },
  ],
};

// Verfügbare Optionen
// Hinweis: Schriftarten die in mPDF standardmäßig verfügbar sind
const FONT_OPTIONS = [
  { value: 'arial', label: 'Sans-Serif (Modern)' },
  { value: 'times', label: 'Serif (Klassisch)' },
  { value: 'courier', label: 'Monospace (Technisch)' },
];

const TABLE_STYLE_OPTIONS = [
  { value: 'bordered', label: 'Mit Rahmen (Standard)' },
  { value: 'minimal', label: 'Minimalistisch' },
  { value: 'striped', label: 'Gestreift (Zebra)' },
];

const HEADER_ACCENT_OPTIONS = [
  { value: 'full', label: 'Volle Breite (Standard)' },
  { value: 'thin', label: 'Dünne Linie' },
  { value: 'none', label: 'Keine' },
];

const FOOTER_STYLE_OPTIONS = [
  { value: 'colored', label: 'Farbiger Hintergrund (Standard)' },
  { value: 'simple', label: 'Einfach mit Linie' },
  { value: 'minimal', label: 'Nur Text' },
];

const FOOTER_LAYOUT_OPTIONS = [
  { value: 'single-line', label: 'Einzeilig (Standard)' },
  { value: 'multi-line', label: 'Mehrzeilig' },
];

const SENDER_LAYOUT_OPTIONS = [
  { value: 'header-left', label: 'Absender oben links (klassisch)' },
  { value: 'above-recipient', label: 'Absender als Rücksende-Zeile über Empfänger (DIN 5008)' },
];

const LOGO_ALIGNMENT_HEADER_OPTIONS = [
  { value: 'right', label: 'Rechts (Standard)' },
  { value: 'center', label: 'Mitte (Seitenmitte)' },
];

const LOGO_ALIGNMENT_ABOVE_RECIPIENT_OPTIONS = [
  { value: 'left', label: 'Links' },
  { value: 'center', label: 'Mitte' },
  { value: 'right', label: 'Rechts (Standard)' },
];

const LINE_SPACING_OPTIONS = [
  { value: 1, label: '1.0 (einzeilig)' },
  { value: 1.15, label: '1.15 (Standard DIN 5008)' },
  { value: 1.2, label: '1.2' },
  { value: 1.3, label: '1.3 (aktuell)' },
];

const LOGO_SIZE_OPTIONS = [
  { value: 'small', label: 'Klein (15mm)' },
  { value: 'medium', label: 'Mittel (20mm, Standard)' },
  { value: 'large', label: 'Groß (30mm)' },
  { value: 'custom', label: 'Benutzerdefiniert' },
];

const NUMBER_FORMAT_OPTIONS = [
  { value: 'auto', label: 'Automatisch (basierend auf Sprache)' },
  { value: 'de', label: 'Deutsch (1.234,56)' },
  { value: 'en', label: 'Englisch (1,234.56)' },
];

// Preset-Farben
const PRESET_COLORS = [
  '#3498db', // Blau (Standard)
  '#2ecc71', // Grün
  '#e74c3c', // Rot
  '#9b59b6', // Lila
  '#f39c12', // Orange
  '#1abc9c', // Türkis
  '#34495e', // Dunkelgrau
  '#e91e63', // Pink
];

const PdfSettingsPage: React.FC = () => {
  const { t } = useTranslation(['account', 'common']);
  const { sellerData, saveSellerData, sellerDataLoading } = useAuth();
  const navigate = useNavigate();
  
  // Lokaler State für Settings
  const [settings, setSettings] = useState<PdfSettings>(DEFAULT_PDF_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  
  // Navigation mit Warnung bei ungespeicherten Änderungen
  const navigateToAccount = useCallback(() => {
    if (hasChanges) {
      setShowNavigationWarning(true);
    } else {
      navigate('/dashboard/konto');
    }
  }, [hasChanges, navigate]);
  
  const handleConfirmNavigation = async (saveFirst: boolean) => {
    if (saveFirst) {
      setIsSaving(true);
      try {
        await saveSellerData({ pdfSettings: settings });
        setHasChanges(false);
      } catch (error) {
        console.error('Fehler beim Speichern:', error);
        setShowNavigationWarning(false);
        return;
      } finally {
        setIsSaving(false);
      }
    }
    setShowNavigationWarning(false);
    navigate('/dashboard/konto');
  };
  
  // Lade Settings aus sellerData (inkl. Legacy-Mapping für logoPosition)
  useEffect(() => {
    if (sellerData?.pdfSettings) {
      const mergedSettings = { ...DEFAULT_PDF_SETTINGS, ...sellerData.pdfSettings };
      const legacyLogoPosition = sellerData.pdfSettings.logoPosition;
      const hasNewLayout =
        sellerData.pdfSettings.senderAddressLayout ||
        sellerData.pdfSettings.logoAlignmentHeader ||
        sellerData.pdfSettings.logoAlignmentAboveRecipient;
      
      if (legacyLogoPosition && !hasNewLayout) {
        if (legacyLogoPosition === 'center') {
          mergedSettings.senderAddressLayout = 'above-recipient';
          mergedSettings.logoAlignmentAboveRecipient = 'center';
        } else {
          mergedSettings.senderAddressLayout = 'header-left';
          mergedSettings.logoAlignmentHeader = legacyLogoPosition === 'left' ? 'center' : 'right';
        }
      }
      
      setSettings(mergedSettings);
    } else {
      setSettings(DEFAULT_PDF_SETTINGS);
    }
    setHasChanges(false);
  }, [sellerData]);
  
  // Update einzelner Settings
  const updateSetting = <K extends keyof PdfSettings>(key: K, value: PdfSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSaveSuccess(false);
  };
  
  // Speichern
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveSellerData({ pdfSettings: settings });
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern der Einstellungen');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Zurücksetzen auf Defaults
  const handleReset = () => {
    if (window.confirm('Möchten Sie alle Einstellungen auf die Standardwerte zurücksetzen?')) {
      setSettings(DEFAULT_PDF_SETTINGS);
      setHasChanges(true);
    }
  };
  
  // Formatiere Zahlen basierend auf Settings
  const formatNumber = useMemo(() => {
    const format = settings.numberFormat === 'auto' ? 'de' : settings.numberFormat;
    return (num: number) => {
      if (format === 'en') {
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
  }, [settings.numberFormat]);
  
  // Berechne Summen für Vorschau
  const totals = useMemo(() => {
    let totalNet = 0;
    const taxSummary: { [rate: number]: { base: number; amount: number } } = {};
    
    PREVIEW_DATA.items.forEach(item => {
      const lineTotal = item.quantity * item.netPrice;
      const taxAmount = lineTotal * (item.taxRate / 100);
      totalNet += lineTotal;
      
      if (!taxSummary[item.taxRate]) {
        taxSummary[item.taxRate] = { base: 0, amount: 0 };
      }
      taxSummary[item.taxRate].base += lineTotal;
      taxSummary[item.taxRate].amount += taxAmount;
    });
    
    const totalTax = Object.values(taxSummary).reduce((sum, t) => sum + t.amount, 0);
    const grandTotal = totalNet + totalTax;
    
    return { totalNet, totalTax, grandTotal, taxSummary };
  }, []);
  
  // Generiere dynamisches CSS für Vorschau
  const previewStyles = useMemo(() => {
    const fontFamilies: Record<string, string> = {
      arial: 'Arial, sans-serif',
      helvetica: 'Arial, sans-serif', // Map to Arial for preview
      roboto: 'Roboto, Arial, sans-serif',
      georgia: 'Georgia, serif',
      times: '"Times New Roman", Times, serif',
      courier: '"Courier New", Courier, monospace',
      dejavusans: 'Arial, sans-serif',
      dejavuserif: '"Times New Roman", Times, serif',
    };
    
    const accentColor = settings.accentColor || '#3498db';
    const fontFamily = fontFamilies[settings.fontFamily || 'arial'];
    
    // Berechne hellere Variante für Zebra-Striping
    const lightenColor = (hex: string, factor: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      const newR = Math.round(r + (255 - r) * factor);
      const newG = Math.round(g + (255 - g) * factor);
      const newB = Math.round(b + (255 - b) * factor);
      return `rgb(${newR}, ${newG}, ${newB})`;
    };
    
    const accentLight = lightenColor(accentColor, 0.9);
    
    const lineSpacing = settings.lineSpacing || 1.3;
    const pageMargins = {
      top: (settings.pageMarginTopMm || 0) || 15,
      right: (settings.pageMarginRightMm || 0) || 15,
      bottom: (settings.pageMarginBottomMm || 0) || 15,
      left: (settings.pageMarginLeftMm || 0) || 15,
    };
    
    return {
      fontFamily,
      accentColor,
      accentLight,
      lineSpacing,
      pageMargins,
      headerAccentHeight: settings.headerAccent === 'full' ? '12px' : settings.headerAccent === 'thin' ? '3px' : '0px',
      headerAccentDisplay: settings.headerAccent === 'none' ? 'none' : 'block',
      tableHeaderBg: settings.tableStyle === 'striped' ? accentColor : settings.tableStyle === 'minimal' ? 'transparent' : '#f2f2f2',
      tableHeaderColor: settings.tableStyle === 'striped' ? 'white' : 'inherit',
      tableBorder: settings.tableStyle === 'minimal' ? 'none' : '1px solid #e0e0e0',
      footerBg: settings.footerStyle === 'colored' ? accentColor : 'transparent',
      footerColor: settings.footerStyle === 'colored' ? 'white' : '#333',
      footerBorder: settings.footerStyle === 'simple' ? `2px solid ${accentColor}` : settings.footerStyle === 'minimal' ? '1px solid #e0e0e0' : 'none',
    };
  }, [settings]);

  if (sellerDataLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-surface-base">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Navigation Warning Modal */}
        {showNavigationWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-surface-raised rounded-lg p-6 max-w-md mx-4 border border-hairline">
              <h3 className="text-lg font-semibold text-primary mb-3">Ungespeicherte Änderungen</h3>
              <p className="text-secondary mb-6">
                Sie haben ungespeicherte Änderungen. Möchten Sie diese speichern, bevor Sie zur Kontoseite wechseln?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleConfirmNavigation(true)}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {isSaving ? 'Speichern...' : 'Speichern & Wechseln'}
                </button>
                <button
                  onClick={() => handleConfirmNavigation(false)}
                  className="flex-1 px-4 py-2 bg-surface-hover text-primary rounded-lg hover:brightness-110 transition-colors"
                >
                  Verwerfen
                </button>
                <button
                  onClick={() => setShowNavigationWarning(false)}
                  className="px-4 py-2 border border-hairline text-secondary rounded-lg hover:bg-surface-inset transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-accent-ink">PDF-Design anpassen</h1>
            <p className="mt-2 text-muted">
              Passen Sie das Erscheinungsbild Ihrer Rechnungen und Angebote an Ihr Corporate Design an.
            </p>
          </div>
          <button
            onClick={navigateToAccount}
            className="flex items-center gap-2 px-4 py-2 bg-surface-inset text-secondary rounded-lg hover:bg-surface-hover transition-colors border border-hairline"
          >
            <IconUser size={14} />
            Zu den Rechnungsdaten
          </button>
        </div>
        
        {/* Erfolgs-Banner */}
        {saveSuccess && (
          <div className="mb-6 p-4 bg-[var(--tone-success-bg)] border border-[var(--tone-success-border)] rounded-lg flex items-center text-[var(--tone-success-fg)]">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Einstellungen erfolgreich gespeichert!
          </div>
        )}
        
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Einstellungen */}
          <div className="space-y-6">
            {/* Farben */}
            <div className="bg-surface-raised rounded-lg border border-hairline p-6">
              <h2 className="text-lg font-semibold text-primary flex items-center mb-4">
                <IconTheme className="mr-2 text-accent-ink" size={18} />
                Farben
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Akzentfarbe
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {PRESET_COLORS.map(color => (
                      <button
                        key={color}
                        onClick={() => updateSetting('accentColor', color)}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          settings.accentColor === color ? 'border-white scale-110' : 'border-strong hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.accentColor || '#3498db'}
                      onChange={(e) => updateSetting('accentColor', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-hairline"
                    />
                    <input
                      type="text"
                      value={settings.accentColor || '#3498db'}
                      onChange={(e) => updateSetting('accentColor', e.target.value)}
                      className="flex-1 px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary placeholder:text-muted"
                      placeholder="#3498db"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Logo */}
            <div className="bg-surface-raised rounded-lg border border-hairline p-6">
              <h2 className="text-lg font-semibold text-primary flex items-center mb-4">
                <IconImage className="mr-2 text-accent-ink" size={18} />
                Logo
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Absender-Position
                  </label>
                  <select
                    value={settings.senderAddressLayout || 'header-left'}
                    onChange={(e) => updateSetting('senderAddressLayout', e.target.value as PdfSettings['senderAddressLayout'])}
                    className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                  >
                    {SENDER_LAYOUT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted">
                    Sie steuern hier, ob der Absender klassisch oben links steht oder als Rücksende-Zeile über der Empfängeradresse (DIN 5008).
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Logo-Position
                  </label>
                  <select
                    value={
                      settings.senderAddressLayout === 'above-recipient'
                        ? (settings.logoAlignmentAboveRecipient || 'right')
                        : (settings.logoAlignmentHeader || 'right')
                    }
                    onChange={(e) => {
                      if (settings.senderAddressLayout === 'above-recipient') {
                        updateSetting('logoAlignmentAboveRecipient', e.target.value as PdfSettings['logoAlignmentAboveRecipient']);
                      } else {
                        updateSetting('logoAlignmentHeader', e.target.value as PdfSettings['logoAlignmentHeader']);
                      }
                    }}
                    className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                  >
                    {(settings.senderAddressLayout === 'above-recipient'
                      ? LOGO_ALIGNMENT_ABOVE_RECIPIENT_OPTIONS
                      : LOGO_ALIGNMENT_HEADER_OPTIONS
                    ).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted">
                    Je nach Absender-Position können Sie das Logo separat ausrichten.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Größe
                    </label>
                    <select
                      value={settings.logoSize || 'medium'}
                      onChange={(e) => updateSetting('logoSize', e.target.value as PdfSettings['logoSize'])}
                      className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                    >
                      {LOGO_SIZE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  {settings.logoSize === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-2">
                        Höhe (mm)
                      </label>
                      <input
                        type="number"
                        min="10"
                        max="50"
                        value={settings.logoHeightMm || 20}
                        onChange={(e) => updateSetting('logoHeightMm' as keyof PdfSettings, parseInt(e.target.value) || 20)}
                        className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                      />
                      <p className="mt-1 text-xs text-muted">
                        Die Breite passt sich proportional an.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Typografie */}
            <div className="bg-surface-raised rounded-lg border border-hairline p-6">
              <h2 className="text-lg font-semibold text-primary flex items-center mb-4">
                <IconFont className="mr-2 text-accent-ink" size={18} />
                Typografie
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Schriftart
                  </label>
                  <select
                    value={settings.fontFamily || 'arial'}
                    onChange={(e) => updateSetting('fontFamily', e.target.value as PdfSettings['fontFamily'])}
                    className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                  >
                    {FONT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Zahlenformat
                  </label>
                  <select
                    value={settings.numberFormat || 'auto'}
                    onChange={(e) => updateSetting('numberFormat', e.target.value as PdfSettings['numberFormat'])}
                    className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                  >
                    {NUMBER_FORMAT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Tabellen */}
            <div className="bg-surface-raised rounded-lg border border-hairline p-6">
              <h2 className="text-lg font-semibold text-primary flex items-center mb-4">
                <IconTable className="mr-2 text-accent-ink" size={18} />
                Tabellen & Layout
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Tabellen-Stil
                  </label>
                  <select
                    value={settings.tableStyle || 'bordered'}
                    onChange={(e) => updateSetting('tableStyle', e.target.value as PdfSettings['tableStyle'])}
                    className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                  >
                    {TABLE_STYLE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Header-Akzent
                  </label>
                  <select
                    value={settings.headerAccent || 'full'}
                    onChange={(e) => updateSetting('headerAccent', e.target.value as PdfSettings['headerAccent'])}
                    className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                  >
                    {HEADER_ACCENT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="bg-surface-raised rounded-lg border border-hairline p-6">
              <h2 className="text-lg font-semibold text-primary mb-4">
                Footer
              </h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Stil
                  </label>
                  <select
                    value={settings.footerStyle || 'colored'}
                    onChange={(e) => updateSetting('footerStyle', e.target.value as PdfSettings['footerStyle'])}
                    className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                  >
                    {FOOTER_STYLE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Layout
                  </label>
                  <select
                    value={settings.footerLayout || 'single-line'}
                    onChange={(e) => updateSetting('footerLayout', e.target.value as PdfSettings['footerLayout'])}
                    className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                  >
                    {FOOTER_LAYOUT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-muted mb-1">Ansprechpartner anzeigen</label>
                  <select
                    value={settings.contactNamePosition || 'none'}
                    onChange={(e) => updateSetting('contactNamePosition', e.target.value as 'none' | 'footer' | 'meta')}
                    className="w-full bg-surface-inset border border-hairline rounded-lg px-3 py-2 text-sm text-secondary focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="none">Nicht anzeigen</option>
                    <option value="meta">Im Infoblock (rechts, bei Rechnungsnr.)</option>
                    <option value="footer">Im Footer</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-muted mb-2">Optionale Angaben ausblenden:</p>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.footerHidePhone || false}
                    onChange={(e) => updateSetting('footerHidePhone', e.target.checked)}
                    className="rounded border-strong bg-surface-inset text-indigo-500 mr-2"
                  />
                  <span className="text-sm text-secondary">Telefonnummer ausblenden</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.footerHideEmail || false}
                    onChange={(e) => updateSetting('footerHideEmail', e.target.checked)}
                    className="rounded border-strong bg-surface-inset text-indigo-500 mr-2"
                  />
                  <span className="text-sm text-secondary">E-Mail ausblenden</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.footerHideWebsite || false}
                    onChange={(e) => updateSetting('footerHideWebsite', e.target.checked)}
                    className="rounded border-strong bg-surface-inset text-indigo-500 mr-2"
                  />
                  <span className="text-sm text-secondary">Website ausblenden</span>
                </label>
              </div>
              
              <p className="mt-3 text-xs text-muted">
                Hinweis: Pflichtangaben wie Firmenname, Adresse, USt-IdNr. und Handelsregisternummer werden immer angezeigt (§14 UStG).
              </p>
            </div>
            
            {/* QR-Code */}
            <div className="bg-surface-raised rounded-lg border border-hairline p-6">
              <h2 className="text-lg font-semibold text-primary flex items-center mb-4">
                <IconQrCode className="mr-2 text-accent-ink" size={18} />
                SEPA QR-Code
              </h2>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.showQrCode !== false}
                  onChange={(e) => updateSetting('showQrCode', e.target.checked)}
                  className="rounded border-strong bg-surface-inset text-indigo-500 mr-2"
                />
                <span className="text-sm text-secondary">SEPA QR-Code auf Rechnungen anzeigen</span>
              </label>
              <p className="mt-2 text-xs text-muted">
                Der QR-Code ermöglicht Ihren Kunden, Zahlungen einfach per Banking-App zu tätigen.
              </p>
            </div>

            {/* DIN 5008 & Layout */}
            <div className="bg-surface-raised rounded-lg border border-hairline p-6">
              <h2 className="text-lg font-semibold text-primary flex items-center mb-4">
                DIN 5008 & Layout
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Zeilenabstand
                    </label>
                    <select
                      value={settings.lineSpacing || 1.3}
                      onChange={(e) => updateSetting('lineSpacing', parseFloat(e.target.value) as PdfSettings['lineSpacing'])}
                      className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                    >
                      {LINE_SPACING_OPTIONS.map(opt => (
                        <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.addressWindowEnabled || false}
                    onChange={(e) => updateSetting('addressWindowEnabled', e.target.checked)}
                    className="rounded border-strong bg-surface-inset text-indigo-500 mr-2"
                  />
                  <span className="text-sm text-secondary">Adressfenster-Position (DIN 5008) aktivieren</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.showReferenceLine || false}
                    onChange={(e) => updateSetting('showReferenceLine', e.target.checked)}
                    className="rounded border-strong bg-surface-inset text-indigo-500 mr-2"
                  />
                  <span className="text-sm text-secondary">Referenzzeilen (Unser Zeichen / Ihr Zeichen) anzeigen</span>
                </label>
                
                {settings.addressWindowEnabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-2">
                        Adressfenster links (mm)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="40"
                        value={settings.addressWindowLeftMm ?? 20}
                        onChange={(e) => updateSetting('addressWindowLeftMm', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-2">
                        Adressfenster oben (mm)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="80"
                        value={settings.addressWindowTopMm ?? 45}
                        onChange={(e) => updateSetting('addressWindowTopMm', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                      />
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Seitenrand oben (mm)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={settings.pageMarginTopMm ?? 0}
                      onChange={(e) => updateSetting('pageMarginTopMm', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Seitenrand rechts (mm)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={settings.pageMarginRightMm ?? 0}
                      onChange={(e) => updateSetting('pageMarginRightMm', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Seitenrand unten (mm)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={settings.pageMarginBottomMm ?? 0}
                      onChange={(e) => updateSetting('pageMarginBottomMm', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Seitenrand links (mm)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={settings.pageMarginLeftMm ?? 0}
                      onChange={(e) => updateSetting('pageMarginLeftMm', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 bg-surface-inset border border-hairline rounded-md text-sm text-primary"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Aktionen */}
            <div className="flex gap-4">
              <button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                  hasChanges && !isSaving
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-surface-inset text-muted cursor-not-allowed'
                }`}
              >
                <IconSave />
                {isSaving ? 'Speichern...' : 'Einstellungen speichern'}
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-3 border border-hairline rounded-lg text-secondary hover:bg-surface-inset transition-colors flex items-center gap-2"
              >
                <IconUndo />
                Zurücksetzen
              </button>
            </div>
          </div>
          
          {/* Live-Vorschau */}
          <div className="xl:sticky xl:top-8">
            <div className="bg-surface-raised rounded-lg border border-hairline p-4">
              <h2 className="text-lg font-semibold text-primary flex items-center mb-4">
                <IconView className="mr-2 text-accent-ink" size={18} />
                Live-Vorschau
              </h2>
              
              {/* Skalierte Vorschau - A4-Seitenverhältnis (210×297mm) */}
              <div 
                className="border border-slate-500 rounded-lg overflow-hidden bg-white shadow-lg"
                style={{ 
                  width: '100%',
                  aspectRatio: '210 / 297',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '181.8%',
                  height: '181.8%',
                  transform: 'scale(0.55)',
                  transformOrigin: 'top left',
                }}>
                  <div 
                    style={{ 
                      fontFamily: previewStyles.fontFamily,
                      fontSize: '10pt',
                      color: '#333',
                      lineHeight: previewStyles.lineSpacing,
                      padding: `${previewStyles.pageMargins.top}mm ${previewStyles.pageMargins.right}mm ${previewStyles.pageMargins.bottom}mm ${previewStyles.pageMargins.left}mm`,
                      paddingBottom: '70px',
                      position: 'relative',
                      height: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                  {/* Header Accent */}
                  <div style={{
                    height: previewStyles.headerAccentHeight,
                    backgroundColor: previewStyles.accentColor,
                    display: previewStyles.headerAccentDisplay,
                    marginBottom: '10px',
                  }} />
                  
                  {/* Bedingtes Header-Layout basierend auf Absender-Layout */}
                  {settings.senderAddressLayout === 'above-recipient' ? (
                    <>
                      {/* DIN 5008 Layout: Logo oben mit freier Ausrichtung */}
                      <div style={{ 
                        textAlign: (settings.logoAlignmentAboveRecipient || 'right') as 'left' | 'center' | 'right',
                        marginBottom: '20px', 
                        padding: '10px 20px',
                      }}>
                        {sellerData?.logoUrl ? (
                          <img 
                            src={sellerData.logoUrl} 
                            alt="Logo" 
                            style={{ 
                              display: 'inline-block',
                              height: settings.logoSize === 'custom' 
                                ? `${(settings.logoHeightMm || 20) * 3.78}px` 
                                : settings.logoSize === 'small' ? '57px' : settings.logoSize === 'large' ? '113px' : '76px',
                              maxWidth: '200px',
                              objectFit: 'contain',
                            }} 
                          />
                        ) : (
                          <div style={{ 
                            display: 'inline-block',
                            padding: '15px 35px', 
                            backgroundColor: '#f0f0f0', 
                            borderRadius: '4px',
                            color: '#666',
                            fontSize: '11px',
                          }}>
                            Ihr Logo
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {settings.logoAlignmentHeader === 'center' ? (
                        /* Klassisches Layout mit zentriertem Logo (3-Spalten-Header) */
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                          <tbody>
                            <tr>
                              <td style={{ width: '35%', verticalAlign: 'top', padding: '0 15px' }}>
                                <div style={{ fontSize: '11pt', fontWeight: 'bold', marginBottom: '5px' }}>Absender</div>
                                <div style={{ lineHeight: '1.4', fontSize: '9pt' }}>
                                  {PREVIEW_DATA.seller.name}<br />
                                  {PREVIEW_DATA.seller.street}<br />
                                  {PREVIEW_DATA.seller.zip} {PREVIEW_DATA.seller.city}
                                </div>
                              </td>
                              <td style={{ width: '30%', verticalAlign: 'top', padding: '0 10px', textAlign: 'center' }}>
                                {sellerData?.logoUrl ? (
                                  <img 
                                    src={sellerData.logoUrl} 
                                    alt="Logo" 
                                    style={{ 
                                      display: 'inline-block',
                                      height: settings.logoSize === 'custom' 
                                        ? `${(settings.logoHeightMm || 20) * 3.78}px` 
                                        : settings.logoSize === 'small' ? '57px' : settings.logoSize === 'large' ? '113px' : '76px',
                                      maxWidth: '180px',
                                      objectFit: 'contain',
                                    }} 
                                  />
                                ) : (
                                  <div style={{ 
                                    display: 'inline-block',
                                    padding: '15px 25px', 
                                    backgroundColor: '#f0f0f0', 
                                    borderRadius: '4px',
                                    color: '#666',
                                    fontSize: '11px',
                                  }}>
                                    Ihr Logo
                                  </div>
                                )}
                              </td>
                              <td style={{ width: '35%' }} />
                            </tr>
                          </tbody>
                        </table>
                      ) : (
                        /* Klassisches Layout: Absender links, Logo rechts */
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                          <tbody>
                            <tr>
                              <td style={{ width: '50%', verticalAlign: 'top', padding: '0 15px' }}>
                                <div style={{ fontSize: '11pt', fontWeight: 'bold', marginBottom: '5px' }}>Absender</div>
                                <div style={{ lineHeight: '1.4', fontSize: '9pt' }}>
                                  {PREVIEW_DATA.seller.name}<br />
                                  {PREVIEW_DATA.seller.street}<br />
                                  {PREVIEW_DATA.seller.zip} {PREVIEW_DATA.seller.city}
                                </div>
                              </td>
                              <td style={{ 
                                width: '50%', 
                                verticalAlign: 'top', 
                                padding: '0 15px',
                                textAlign: 'right',
                              }}>
                                {sellerData?.logoUrl ? (
                                  <img 
                                    src={sellerData.logoUrl} 
                                    alt="Logo" 
                                    style={{ 
                                      display: 'inline-block',
                                      height: settings.logoSize === 'custom' 
                                        ? `${(settings.logoHeightMm || 20) * 3.78}px` 
                                        : settings.logoSize === 'small' ? '57px' : settings.logoSize === 'large' ? '113px' : '76px',
                                      maxWidth: '180px',
                                      objectFit: 'contain',
                                    }} 
                                  />
                                ) : (
                                  <div style={{ 
                                    display: 'inline-block',
                                    padding: '15px 25px', 
                                    backgroundColor: '#f0f0f0', 
                                    borderRadius: '4px',
                                    color: '#666',
                                    fontSize: '11px',
                                  }}>
                                    Ihr Logo
                                  </div>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </>
                  )}
                  
                  {/* Titel */}
                  <div style={{ textAlign: 'center', fontSize: '24pt', fontWeight: 'bold', margin: '15px 0' }}>
                    RECHNUNG
                  </div>
                  
                  <div style={{ height: '2px', backgroundColor: '#e0e0e0', margin: '5px 0 15px' }} />
                  
                  {/* Empfänger & Infos */}
                  <div style={{ display: 'flex', padding: '0 20px', marginBottom: '20px' }}>
                    <div style={{ 
                      width: '50%',
                      paddingTop: settings.addressWindowEnabled && settings.senderAddressLayout === 'above-recipient'
                        ? `${settings.addressWindowTopMm ?? 45}mm`
                        : undefined,
                      paddingLeft: settings.addressWindowEnabled && settings.senderAddressLayout === 'above-recipient'
                        ? `${settings.addressWindowLeftMm ?? 20}mm`
                        : undefined,
                    }}>
                      {settings.senderAddressLayout === 'above-recipient' && (
                        <div style={{ 
                          fontSize: '7pt', 
                          color: '#666666', 
                          marginBottom: '8px',
                          lineHeight: '1.2',
                          textDecoration: 'underline',
                        }}>
                          {PREVIEW_DATA.seller.name} · {PREVIEW_DATA.seller.street}, {PREVIEW_DATA.seller.zip} {PREVIEW_DATA.seller.city}
                        </div>
                      )}
                      <div style={{ lineHeight: '1.3' }}>
                        {PREVIEW_DATA.buyer.companyName}<br />
                        {PREVIEW_DATA.buyer.street}<br />
                        {PREVIEW_DATA.buyer.zip} {PREVIEW_DATA.buyer.city}
                      </div>
                    </div>
                    <div style={{ width: '50%', textAlign: 'right' }}>
                      {settings.senderAddressLayout === 'above-recipient' && (
                        <div style={{ 
                          fontSize: '7pt', 
                          marginBottom: '8px',
                          lineHeight: '1.2',
                          visibility: 'hidden',
                        }}>
                          {PREVIEW_DATA.seller.name} · {PREVIEW_DATA.seller.street}, {PREVIEW_DATA.seller.zip} {PREVIEW_DATA.seller.city}
                        </div>
                      )}
                      <div style={{ fontSize: '9pt', lineHeight: '1.6' }}>
                        <div><strong>Rechnungsnr.:</strong> {PREVIEW_DATA.invoiceNumber}</div>
                        <div><strong>Datum:</strong> {PREVIEW_DATA.invoiceDate}</div>
                        <div><strong>Fällig am:</strong> {PREVIEW_DATA.dueDate}</div>
                        <div><strong>USt-IdNr.:</strong> {PREVIEW_DATA.seller.vatNumber}</div>
                        <div><strong>Handelsregister:</strong> {PREVIEW_DATA.seller.commercialRegisterNumber}</div>
                        <div style={{ marginLeft: '0' }}>({PREVIEW_DATA.seller.commercialRegisterLocation})</div>
                        <div><strong>Liefer-/Leistungsdatum:</strong> {PREVIEW_DATA.deliveryDate}</div>
                        {settings.contactNamePosition === 'meta' && (
                          <div><strong>Ansprechpartner:</strong> {PREVIEW_DATA.seller.contact.name}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Positionen-Tabelle */}
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse', 
                    margin: '20px 0',
                  }}>
                    <thead>
                      <tr style={{ 
                        backgroundColor: previewStyles.tableHeaderBg,
                        color: previewStyles.tableHeaderColor,
                      }}>
                        <th style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'left', width: '30px' }}>Pos.</th>
                        <th style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'left' }}>Beschreibung</th>
                        <th style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'center' }}>Menge</th>
                        <th style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'center' }}>Einheit</th>
                        <th style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'right' }}>Einzelpreis<br /><span style={{ fontWeight: 'normal', fontSize: '8pt' }}>(EUR)</span></th>
                        <th style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'center' }}>MwSt.</th>
                        <th style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'right' }}>Gesamt<br /><span style={{ fontWeight: 'normal', fontSize: '8pt' }}>(EUR)</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {PREVIEW_DATA.items.map((item, idx) => (
                        <tr 
                          key={idx} 
                          style={{ 
                            backgroundColor: settings.tableStyle === 'striped' && idx % 2 === 1 ? previewStyles.accentLight : 'transparent',
                          }}
                        >
                          <td style={{ border: previewStyles.tableBorder, padding: '6px 8px' }}>{idx + 1}</td>
                          <td style={{ border: previewStyles.tableBorder, padding: '6px 8px' }}>
                            {item.name}
                            {item.description && <div style={{ fontSize: '9pt', color: '#555' }}>{item.description}</div>}
                          </td>
                          <td style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'center' }}>
                            {formatNumber(item.quantity)}
                          </td>
                          <td style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'center' }}>
                            {item.unitCode === 'HUR' ? 'Stunde' : 'Stück'}
                          </td>
                          <td style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'right' }}>
                            {formatNumber(item.netPrice)}
                          </td>
                          <td style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'center' }}>
                            {item.taxRate} %
                          </td>
                          <td style={{ border: previewStyles.tableBorder, padding: '6px 8px', textAlign: 'right' }}>
                            {formatNumber(item.quantity * item.netPrice)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Steuerübersicht & Summen */}
                  <div style={{ padding: '0 15px', marginBottom: '15px' }}>
                    <div style={{ fontSize: '9pt', marginBottom: '4px' }}>
                      <strong>Steuerübersicht:</strong>
                      {Object.entries(totals.taxSummary).map(([rate, { amount, base }]) => (
                        <span key={rate} style={{ marginLeft: '8px' }}>
                          zzgl. {rate},00% MwSt. auf {formatNumber(base)} EUR = {formatNumber(amount)} EUR
                        </span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <table style={{ borderCollapse: 'collapse', width: '280px' }}>
                        <tbody>
                          <tr>
                            <td style={{ padding: '4px 8px', fontSize: '9pt', borderBottom: '1px solid #e0e0e0' }}>Nettobetrag</td>
                            <td style={{ padding: '4px 8px', fontSize: '9pt', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>{formatNumber(totals.totalNet)} EUR</td>
                          </tr>
                          {Object.entries(totals.taxSummary).map(([rate, { amount }]) => (
                            <tr key={rate}>
                              <td style={{ padding: '4px 8px', fontSize: '9pt', borderBottom: '1px solid #e0e0e0' }}>MwSt. ({rate},00 %)</td>
                              <td style={{ padding: '4px 8px', fontSize: '9pt', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>{formatNumber(amount)} EUR</td>
                            </tr>
                          ))}
                          <tr>
                            <td style={{ padding: '6px 8px', fontSize: '11pt', fontWeight: 'bold', borderTop: '2px solid #333' }}>Gesamtbetrag</td>
                            <td style={{ padding: '6px 8px', fontSize: '11pt', fontWeight: 'bold', textAlign: 'right', borderTop: '2px solid #333' }}>{formatNumber(totals.grandTotal)} EUR</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Zahlungsbereich: Bankinfos links, QR-Code rechts */}
                  <div style={{ 
                    display: 'flex', 
                    padding: '10px 15px', 
                    marginBottom: '10px',
                    borderTop: '1px solid #e0e0e0',
                    gap: '20px',
                  }}>
                    {/* Bankinfos links */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '10pt', marginBottom: '5px' }}>Zahlungsinformationen</div>
                      <div style={{ fontSize: '9pt', lineHeight: '1.5' }}>
                        <div>Kontoinhaber: {PREVIEW_DATA.seller.name}</div>
                        <div>IBAN: {PREVIEW_DATA.seller.bankDetails.iban}</div>
                        <div>BIC: {PREVIEW_DATA.seller.bankDetails.bic}</div>
                        <div>Bank: {PREVIEW_DATA.seller.bankDetails.bankName}</div>
                      </div>
                      <div style={{ marginTop: '6px', fontSize: '9pt', lineHeight: '1.5' }}>
                        <div><strong>Zahlungsziel:</strong> Bitte zahlen Sie innerhalb von 2 Wochen ohne den Abzug von Skonto.</div>
                        <div>Bitte geben Sie bei der Überweisung die Rechnungsnummer {PREVIEW_DATA.invoiceNumber} an.</div>
                      </div>
                      <div style={{ marginTop: '6px', fontSize: '9pt', lineHeight: '1.5' }}>
                        <strong>Anmerkungen:</strong>
                        <div>Vielen Dank für Ihren Auftrag.</div>
                      </div>
                    </div>
                    
                    {/* QR-Code rechts */}
                    {settings.showQrCode !== false && (
                      <div style={{ 
                        width: '90px',
                        padding: '8px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        textAlign: 'center',
                        flexShrink: 0,
                      }}>
                        <svg width="70" height="70" viewBox="0 0 80 80" style={{ display: 'block', margin: '0 auto' }}>
                          <rect width="80" height="80" fill="white"/>
                          <rect x="4" y="4" width="24" height="24" fill="black"/>
                          <rect x="8" y="8" width="16" height="16" fill="white"/>
                          <rect x="12" y="12" width="8" height="8" fill="black"/>
                          <rect x="52" y="4" width="24" height="24" fill="black"/>
                          <rect x="56" y="8" width="16" height="16" fill="white"/>
                          <rect x="60" y="12" width="8" height="8" fill="black"/>
                          <rect x="4" y="52" width="24" height="24" fill="black"/>
                          <rect x="8" y="56" width="16" height="16" fill="white"/>
                          <rect x="12" y="60" width="8" height="8" fill="black"/>
                          <rect x="32" y="4" width="4" height="4" fill="black"/>
                          <rect x="40" y="8" width="4" height="4" fill="black"/>
                          <rect x="36" y="16" width="4" height="4" fill="black"/>
                          <rect x="44" y="20" width="4" height="4" fill="black"/>
                          <rect x="32" y="32" width="4" height="4" fill="black"/>
                          <rect x="40" y="36" width="4" height="4" fill="black"/>
                          <rect x="48" y="40" width="4" height="4" fill="black"/>
                          <rect x="56" y="36" width="4" height="4" fill="black"/>
                          <rect x="64" y="44" width="4" height="4" fill="black"/>
                          <rect x="36" y="48" width="4" height="4" fill="black"/>
                          <rect x="44" y="52" width="4" height="4" fill="black"/>
                          <rect x="52" y="56" width="4" height="4" fill="black"/>
                          <rect x="60" y="64" width="4" height="4" fill="black"/>
                          <rect x="68" y="68" width="4" height="4" fill="black"/>
                        </svg>
                        <div style={{ fontSize: '7pt', color: '#666', marginTop: '3px', lineHeight: '1.3' }}>
                          SEPA QR-Code<br />
                          Einfach mit Banking-App scannen.
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Footer */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: previewStyles.footerBg,
                    color: previewStyles.footerColor,
                    borderTop: previewStyles.footerBorder,
                    textAlign: 'center',
                    padding: settings.footerLayout === 'multi-line' ? '8px 10px' : '6px 10px',
                    fontSize: '8pt',
                    lineHeight: settings.footerLayout === 'multi-line' ? '1.5' : '1.3',
                  }}>
                    {settings.footerLayout === 'multi-line' ? (
                      <>
                        <div style={{ fontWeight: 500 }}>{PREVIEW_DATA.seller.name}</div>
                        <div>{PREVIEW_DATA.seller.street}, {PREVIEW_DATA.seller.zip} {PREVIEW_DATA.seller.city}</div>
                        <div>
                          {settings.contactNamePosition === 'footer' && `Ansprechpartner: ${PREVIEW_DATA.seller.contact.name}`}
                          {settings.contactNamePosition === 'footer' && !settings.footerHidePhone && ' | '}
                          {!settings.footerHidePhone && `Tel: ${PREVIEW_DATA.seller.contact.phone}`}
                          {(settings.contactNamePosition === 'footer' || !settings.footerHidePhone) && !settings.footerHideEmail && ' | '}
                          {!settings.footerHideEmail && PREVIEW_DATA.seller.contact.email}
                          {(settings.contactNamePosition === 'footer' || !settings.footerHidePhone || !settings.footerHideEmail) && !settings.footerHideWebsite && ' | '}
                          {!settings.footerHideWebsite && PREVIEW_DATA.seller.contact.website}
                        </div>
                        <div>USt-IdNr: {PREVIEW_DATA.seller.vatNumber} | {PREVIEW_DATA.seller.commercialRegisterNumber}, Amtsgericht {PREVIEW_DATA.seller.commercialRegisterLocation}</div>
                      </>
                    ) : (
                      <div style={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        padding: '0 5px',
                      }}>
                        {PREVIEW_DATA.seller.name} | {PREVIEW_DATA.seller.street}, {PREVIEW_DATA.seller.zip} {PREVIEW_DATA.seller.city}
                        {settings.contactNamePosition === 'footer' && ` | Ansprechpartner: ${PREVIEW_DATA.seller.contact.name}`}
                        {!settings.footerHidePhone && ` | Tel: ${PREVIEW_DATA.seller.contact.phone}`}
                        {!settings.footerHideEmail && ` | ${PREVIEW_DATA.seller.contact.email}`}
                        {!settings.footerHideWebsite && ` | ${PREVIEW_DATA.seller.contact.website}`}
                        {` | USt-IdNr: ${PREVIEW_DATA.seller.vatNumber}`}
                        {` | ${PREVIEW_DATA.seller.commercialRegisterNumber}, Amtsgericht ${PREVIEW_DATA.seller.commercialRegisterLocation}`}
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>
              
              <p className="mt-4 text-xs text-muted text-center">
                Dies ist eine vereinfachte Vorschau. Das endgültige PDF kann leicht abweichen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfSettingsPage;
